/** SA-1080: the router editor's shared form parts: the labeled field, the control classes and
 *  the handler select with the picker its handler needs. The kind cards, the built-in cards and
 *  the Add kind form all draw them. */
import { useId, type ReactNode } from 'react'
import { outcomeLabel } from '@/components/process/stepLiveText'
import type { MailboxHandler } from '@/services/mailboxes'
import { CLOSE_OUTCOME_CHOICES, toggleOutcome, type KindDoc } from './routerPolicyModel'

export const SELECT = 'w-full text-sm rounded-md border border-border bg-background px-2 py-1.5 text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring'
export const AREA = 'w-full text-sm rounded-md border border-border bg-background px-2 py-1.5 text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring'
export const LABEL = 'mb-1 block text-xs font-medium text-muted-foreground'

export function Field({ label, children, error }: { label: string; children: (id: string) => ReactNode; error?: ReactNode }) {
  const id = useId()
  return (
    <div className="min-w-0">
      <label htmlFor={id} className={LABEL}>{label}</label>
      {children(id)}
      {error}
    </div>
  )
}

export interface HandlerFieldsProps {
  prefix: string
  entry: Partial<KindDoc>
  onChange: (changes: Partial<KindDoc>) => void
  handlers: MailboxHandler[]
  /** What the channel offers; a loaded pick outside it stays shown. */
  offered: MailboxHandler[]
  agendas: Array<{ workflow_id: string; name: string }>
  workflows: Array<{ id: string; name: string }>
  /** The server's sentence for a slot, or null. */
  at: (slot: string) => ReactNode
}

/** The handler select and, when the handler picks a workflow, its picker. */
export function HandlerFields({ prefix, entry, onChange: change, handlers, offered, agendas, workflows, at }: HandlerFieldsProps) {
  const handlerOf = (id: string) => handlers.find((handler) => handler.id === id)
  const config = handlerOf(entry.handler ?? '')?.config ?? null
  const bound = typeof entry.handler_config?.workflow_id === 'string' ? entry.handler_config.workflow_id : ''
  const options = config === 'agenda'
    ? agendas.map((agenda) => ({ id: agenda.workflow_id, name: agenda.name }))
    : workflows
  return (
    <>
      <Field label="Handler" error={at(`${prefix}:handler`)}>
        {(id) => (
          <select id={id} className={SELECT} value={entry.handler ?? ''}
            onChange={(e) => change({ handler: e.target.value, handler_config: {} })}>
            {!offered.some((handler) => handler.id === entry.handler) && (
              <option value={entry.handler ?? ''}>{handlerOf(entry.handler ?? '')?.label ?? (entry.handler || 'Select a handler…')}</option>
            )}
            {offered.map((handler) => <option key={handler.id} value={handler.id}>{handler.label}</option>)}
          </select>
        )}
      </Field>
      {config === 'outcomes' && (() => {
        const stored = Array.isArray(entry.handler_config?.outcomes)
          ? entry.handler_config.outcomes.filter((c): c is string => typeof c === 'string') : []
        const codes = [...CLOSE_OUTCOME_CHOICES, ...stored.filter((c) => !CLOSE_OUTCOME_CHOICES.includes(c))]
        return (
          <fieldset className="space-y-1">
            <legend className="text-sm font-medium">Outcomes it may close as</legend>
            {codes.map((code) => (
              <label key={code} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={stored.includes(code)}
                  onChange={(e) => change({ handler_config: toggleOutcome(entry.handler_config ?? {}, code, e.target.checked) })} />
                {outcomeLabel(code)}
              </label>
            ))}
            {at(`${prefix}:handler_config`)}
          </fieldset>
        )
      })()}
      {config && config !== 'outcomes' && (
        <Field label={config === 'agenda' ? 'Intake agenda' : 'Workflow'} error={at(`${prefix}:handler_config`)}>
          {(id) => (
            <select id={id} className={SELECT} value={bound}
              onChange={(e) => change({ handler_config: e.target.value ? { workflow_id: e.target.value } : {} })}>
              <option value="">Select…</option>
              {bound && !options.some((option) => option.id === bound) && <option value={bound}>{bound}</option>}
              {options.map((option) => <option key={option.id} value={option.id}>{option.name} ({option.id})</option>)}
            </select>
          )}
        </Field>
      )}
      {!config && at(`${prefix}:handler_config`)}
    </>
  )
}
